import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useInterval } from "@/utils";
import { ProjectFileUploader } from "@/utils/projectFileUploader";
import { MediaServices } from "@/services/MediaServices";
import { Media } from "@/services/bedrock";
import fixWebmDuration from "fix-webm-duration";

export function useMediaRecorder(
  webcamRef: React.RefObject<any>,
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  bgType: "none" | "blur" | "gradient" | "image",
  mimeType: string,
  videoType: string,
  scrollToRef: React.RefObject<HTMLTextAreaElement | null>,
  setLoading: (val: boolean) => void,
  setLoadingProgress: (val: number | undefined) => void,
  setLoadingLabel: (val: string) => void,
  onDone: (media: Media, secondsRecorded: number) => void
) {
  const [recordingState, setRecordingState] = useState<"toStart" | "Review" | "Recording" | "Done">("toStart");
  const [recorder, setRecorder] = useState<MediaRecorder | undefined>(undefined);
  const [recordedBlob, setRecordedBlob] = useState<Blob | undefined>(undefined);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [displaySeconds, setDisplaySeconds] = useState(0);
  const [dataReady, setDataReady] = useState(true);

  const chunksRef = useRef<Blob[]>([]);
  const recordedDurationMs = useRef(0);
  const segmentStartTimeRef = useRef(0);
  const scrollAccumulator = useRef(0);

  const isRecording = useMemo(() => recordingState === "Recording", [recordingState]);

  useInterval(() => {
    if (isRecording) {
      const elapsedSinceSegmentStart = Date.now() - segmentStartTimeRef.current;
      const totalElapsedMs = recordedDurationMs.current + elapsedSinceSegmentStart;
      const secondsRecorded = Math.floor(totalElapsedMs / 1000);
      setDisplaySeconds(secondsRecorded);
    }
  }, 1000);

  const onStartOver = useCallback(() => {
    if (recorder) {
      if (recorder.state !== "inactive") {
        recorder.stop();
      }
    }

    setRecorder(undefined);
    setRecordingState("toStart");
    chunksRef.current = [];
    setRecordedBlob(undefined);
    setPreviewUrl(undefined);
    setDataReady(true);
    setDisplaySeconds(0);

    recordedDurationMs.current = 0;
    segmentStartTimeRef.current = 0;
    scrollAccumulator.current = 0;

    const currentRef = scrollToRef?.current;
    if (currentRef) {
      currentRef.scrollTop = 0;
    }
  }, [recorder, scrollToRef]);

  useEffect(() => {
    if (!recorder) return;

    const handleDataAvailable = (event: BlobEvent) => {
      if (event.data && event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    const handleStop = async () => {
      if (chunksRef.current.length > 0) {
        let blob = new Blob(chunksRef.current, { type: videoType });

        if (mimeType.includes("webm")) {
          blob = await fixWebmDuration(blob, recordedDurationMs.current);
        }

        const url = URL.createObjectURL(blob);
        setPreviewUrl(url);
        setRecordedBlob(blob);
        setDataReady(true);
      }
    };

    recorder.addEventListener("dataavailable", handleDataAvailable);
    recorder.addEventListener("stop", handleStop);

    return () => {
      recorder.removeEventListener("dataavailable", handleDataAvailable);
      recorder.removeEventListener("stop", handleStop);
    };
  }, [recorder, mimeType, videoType]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const createRecorder = useCallback(() => {
    const stream = webcamRef?.current?.stream;
    if (!stream) return undefined;

    let streamToRecord = stream;

    if (bgType !== "none" && canvasRef.current) {
      const canvasStream = canvasRef.current.captureStream(30);
      const audioTracks = stream.getAudioTracks();
      audioTracks.forEach((track: MediaStreamTrack) => {
        canvasStream.addTrack(track);
      });
      streamToRecord = canvasStream;
    }

    const newRecorderInstance = new MediaRecorder(streamToRecord, {
      mimeType,
      audioBitsPerSecond: 128000,
      videoBitsPerSecond: 2500000,
    });
    setRecorder(newRecorderInstance);
    return newRecorderInstance;
  }, [webcamRef, canvasRef, bgType, mimeType]);

  const onStartRecording = useCallback(() => {
    const stream = webcamRef?.current?.stream;

    if (!stream) {
      console.error("Attempted to start recording, but no stream is available.");
      return;
    }

    if (stream.getAudioTracks().length === 0) {
      const proceed = window.confirm(
        "We couldn't detect an active microphone. If you continue, your video will be silent. Do you want to record anyway?"
      );
      if (!proceed) return;
    }

    const recorderInstance = createRecorder();
    if (!recorderInstance) return;

    recordedDurationMs.current = 0;
    setDisplaySeconds(0);
    segmentStartTimeRef.current = Date.now();

    recorderInstance.start(1000);
    setRecordingState("Recording");
  }, [webcamRef, createRecorder]);

  const onStopRecording = useCallback(async () => {
    setDataReady(false);
    const segmentDuration = Date.now() - segmentStartTimeRef.current;
    recordedDurationMs.current += segmentDuration;

    recorder?.stop();
    setRecordingState("Review");
  }, [recorder]);

  const transcode = async (media: Media) => {
    try {
      setLoadingProgress(undefined);
      setLoadingLabel("Processing media. This may take a minute...");
      let job = await MediaServices.transcode({ mediaUrl: media.uri });
      const jobId = job.jobName;
      while (job.jobStatus !== "SUCCEEDED") {
        await new Promise(r => setTimeout(r, 2000));
        job = await MediaServices.getJobStatus(jobId);
      }

      return {
        ...media,
        uri: job.downloadLink,
        extension: "mp4",
        type: "video/mp4",
      } as Media;
    } catch (e) {
      console.error("Error transcoding:", e);
      throw e;
    }
  };

  const onDoneRecording = useCallback(async () => {
    setRecordingState("Done");

    const uploader = new ProjectFileUploader("teleprompter");
    let ext = videoType.split("/")[1];
    setLoading(true);
    setLoadingProgress(0);
    setLoadingLabel("Uploading");

    const media: Media = {
      id: 0,
      sortOrder: 0,
      category: "VIDEO",
      fileName: `teleprompter_${new Date().getTime()}`,
      uri: previewUrl || "",
      type: videoType,
      extension: ext,
      imageFile: recordedBlob,
    };

    const uploaded = await uploader.prepareMedia([media], (_, progress) => {
      setLoadingProgress(Math.trunc(progress * 100));
    });

    let newMedia = uploaded[0];
    if (ext === "webm") {
      newMedia = await transcode(newMedia);
    }
    setLoading(false);
    onDone(newMedia, displaySeconds);
  }, [displaySeconds, onDone, videoType, previewUrl, recordedBlob, setLoading, setLoadingProgress, setLoadingLabel]);

  return {
    recordingState,
    setRecordingState,
    recorder,
    setRecorder,
    recordedBlob,
    setRecordedBlob,
    previewUrl,
    setPreviewUrl,
    displaySeconds,
    setDisplaySeconds,
    dataReady,
    setDataReady,
    isRecording,
    onStartRecording,
    onStopRecording,
    onStartOver,
    onDoneRecording,
  };
}
